import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ROIChart } from "@/components/charts/ROIChart";
import { PnLChart } from "@/components/charts/PnLChart";
import { RevenueChart } from "@/components/charts/RevenueChart";
import { ProfitMarginChart } from "@/components/charts/ProfitMarginChart";
import { ProfitPerAttendeeChart } from "@/components/charts/ProfitPerAttendeeChart";
import { CostCoverageChart } from "@/components/charts/CostCoverageChart";
import { cn } from "@/lib/utils";
import type { ScenarioMetrics } from "@satyrsmc/shared/client";

function getTabColor(metrics: ScenarioMetrics[]): "profit" | "breakEven" | "loss" {
  const anyMeetsTarget = metrics.some((m) => m.profitVsBreakEven >= 0);
  const anyProfitable = metrics.some((m) => m.profit >= 0);
  if (anyMeetsTarget) return "profit";
  if (anyProfitable) return "breakEven";
  return "loss";
}

interface ScenarioMatrixTableProps {
  metrics: ScenarioMetrics[];
  profitTarget?: number;
}

function SingleScenarioTable({
  title,
  metrics,
  profitTarget,
}: {
  title: string;
  metrics: ScenarioMetrics[];
  profitTarget?: number;
}) {
  const sorted = [...metrics].sort((a, b) => {
    if (a.profit !== b.profit) return a.profit - b.profit;
    if (a.ticketPrice !== b.ticketPrice) return a.ticketPrice - b.ticketPrice;
    return a.staffPrice - b.staffPrice;
  });
  const showCharts = metrics.length > 1 && metrics.length <= 12;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>
            {metrics[0]?.attendees} attendees. Gross = ticket income; Net = Gross − costs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket Cost</TableHead>
                  <TableHead>Staff Cost</TableHead>
                  <TableHead>Attendance</TableHead>
                  <TableHead className="text-right">Gross Revenue</TableHead>
                  <TableHead className="text-right">Net Revenue</TableHead>
                  <TableHead className="text-right">Profit Margin</TableHead>
                  <TableHead className="text-right">Profit/Attendee</TableHead>
                  <TableHead className="text-right">Cost Coverage</TableHead>
                  <TableHead className="text-right">Rev/Ticket</TableHead>
                  <TableHead className="text-right">Attendee %</TableHead>
                  <TableHead className="text-right">Break-ev Att %</TableHead>
                  <TableHead className="text-right">Target Cov %</TableHead>
                  <TableHead className="text-right">Meets target</TableHead>
                  <TableHead className="text-right">Cost/person</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((m) => (
                  <TableRow key={m.scenarioKey} className={cn(m.profit < 0 && "bg-destructive/5")}>
                    <TableCell className="max-sm:py-2.5">${m.ticketPrice}</TableCell>
                    <TableCell className="max-sm:py-2.5">${m.staffPrice}</TableCell>
                    <TableCell className="max-sm:py-2.5">{m.attendancePercent}%</TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      ${m.revenue.toLocaleString()}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums font-medium max-sm:py-2.5",
                        m.profit >= 0 ? "text-green-600" : "text-red-600",
                      )}
                    >
                      ${m.profit.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      {m.profitMargin.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      ${m.profitPerAttendee.toFixed(0)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      {m.costCoverageRatio.toFixed(2)}×
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      ${m.avgRevenuePerTicket.toFixed(0)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      {m.revenueMixAttendee.toFixed(0)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      {m.breakEvenAttendancePercent != null
                        ? `${Math.round(m.breakEvenAttendancePercent)}%`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      {m.profitTargetCoverage != null
                        ? `${m.profitTargetCoverage.toFixed(0)}%`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right max-sm:py-2.5">
                      {m.profitVsBreakEven >= 0 ? (
                        <span className="text-green-600">Yes</span>
                      ) : (
                        <span className="text-amber-600">No</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-sm:py-2.5">
                      ${m.costPerAttendee.toFixed(0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {showCharts && (
        <section className="grid gap-6 md:grid-cols-2">
          <ROIChart metrics={metrics} />
          <PnLChart metrics={metrics} profitTarget={profitTarget} />
          <RevenueChart metrics={metrics} />
          <ProfitMarginChart metrics={metrics} />
          <ProfitPerAttendeeChart metrics={metrics} />
          <CostCoverageChart metrics={metrics} />
        </section>
      )}
    </div>
  );
}

export function ScenarioMatrixTable({ metrics, profitTarget }: ScenarioMatrixTableProps) {
  const byAttendance = metrics.reduce(
    (acc, m) => {
      const key = m.attendancePercent;
      if (!acc[key]) acc[key] = [];
      acc[key].push(m);
      return acc;
    },
    {} as Record<number, ScenarioMetrics[]>,
  );

  const attendanceLevels = Object.keys(byAttendance)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((pct) => pct >= 50);

  const firstProfitableTab = String(
    attendanceLevels.find((pct) => byAttendance[pct].some((m) => m.profit >= 0)) ??
      attendanceLevels[0],
  );

  const [activeTab, setActiveTab] = useState(firstProfitableTab);
  useEffect(() => {
    setActiveTab(firstProfitableTab);
  }, [firstProfitableTab]);

  const tabColorClasses = {
    profit:
      "!text-green-600 hover:!text-green-600 data-[state=active]:!text-green-600 data-[state=active]:border-green-500 data-[state=active]:bg-green-500/10 data-[state=active]:after:bg-green-500",
    breakEven:
      "!text-amber-600 hover:!text-amber-600 data-[state=active]:!text-amber-600 data-[state=active]:border-amber-500 data-[state=active]:bg-amber-500/10 data-[state=active]:after:bg-amber-500",
    loss: "!text-red-600 hover:!text-red-600 data-[state=active]:!text-red-600 data-[state=active]:border-red-500 data-[state=active]:bg-red-500/10 data-[state=active]:after:bg-red-500",
  };

  if (attendanceLevels.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Scenario matrix</h2>
        <p className="text-muted-foreground text-sm">No scenarios to display.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Scenario matrix</h2>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex flex-wrap gap-1">
          {attendanceLevels.map((pct) => {
            const tabMetrics = byAttendance[pct];
            const color = getTabColor(tabMetrics);
            return (
              <TabsTrigger
                key={pct}
                value={String(pct)}
                className={cn("font-medium", tabColorClasses[color])}
              >
                {pct}% attendance
              </TabsTrigger>
            );
          })}
        </TabsList>
        {attendanceLevels.map((pct) => (
          <TabsContent key={pct} value={String(pct)} className="mt-4">
            <SingleScenarioTable
              title={`${pct}% attendance`}
              metrics={byAttendance[pct]}
              profitTarget={profitTarget}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
